
<template>
    <div class="card">
        <Button rounded label="Add Group" icon="pi pi-plus" class="mb-1" @click="addGroup" data-cy="add-group" />
        <Dialog header="Add Group" v-model:visible="showDialog">
            <div class="p-fluid">
                <div class="p-field gap-1 flex items-center">
                    <label for="name">Name</label>
                    <InputText id="name" v-model="group.name" />
                </div>
            </div>
            <template #footer>
                <Button rounded label="Cancel" icon="pi pi-times" class="p-button-text" @click="showDialog = false" data-cy="add-group-cancel" />
                <Button rounded label="Save" icon="pi pi-check" @click="saveGroup" data-cy="add-group-save"/>
            </template>
        </Dialog>
        <Dialog v-model:visible="showDeleteDialog" header="Delete Group">
            <div class="mb-1">
                Are you sure you want to delete this group?
            </div>
            <template #footer>
                <Button rounded label="Cancel" icon="pi pi-times" class="p-button-text" @click="showDeleteDialog = false" data-cy="add-group-cancel" />
                <Button rounded icon="pi pi-check" class="ml-1" label="Delete" @click="deleteGroup" data-cy="delete-group-confirm"/>
            </template>
        </Dialog>
        <DataTable sortField="name" :sortOrder="1" v-model:filters="filters" :value="groups" paginator showGridlines :rows="10" dataKey="id"
                   scrollable scrollHeight="calc(100vh - 350px)"
                filterDisplay="menu" :loading="loading" :globalFilterFields="['name']">
            <template #header>
                <div class="flex justify-between">
                    <Button rounded type="button" icon="pi pi-filter-slash" label="Clear" outlined @click="clearFilter()" data-cy="clear-filters"/>
                    <IconField iconPosition="left">
                        <InputIcon>
                            <i class="pi pi-search" />
                        </InputIcon>
                        <InputText v-model="filters['global'].value" placeholder="Keyword Search" data-cy="keyword-search" />
                    </IconField>
                </div>
            </template>
            <template #empty> No groups found. </template>
            <template #loading> Loading groups data. Please wait. </template>
            <Column sortable field="name" header="Name" style="min-width: 12rem">
                <template #body="{ data }">
                    {{ data.name }}
                </template>
                <template #filter="{ filterModel }">
                    <InputText v-model="filterModel.value" type="text" class="p-column-filter" placeholder="Search by name" />
                </template>
            </Column>
            <Column header="Users" field="users" :filterMenuStyle="{ width: '14rem' }" style="min-width: 12rem">
                <template #body="{ data }">
                    <div class="flex gap-1" v-if="selectedActionUserId != data.id || editType != 'users'">
                        <i class="pi pi-pencil cursor-pointer" @click="editUsers(data)" data-cy="edit-users"></i>
                        <Tag v-for="user in data.users" :value="user.email" />
                    </div>
                    <div v-else>
                        <InputGroup v-if="!updatingUser">
                            <MultiSelect v-model="usersToEdit" display="chip" :options="users" optionValue="id" optionLabel="email" placeholder="Select Users"
                            :maxSelectedLabels="3" class="w-full md:w-80" />
                            <Button icon="pi pi-check" severity="success" @click="updateUsers"/>
                            <Button icon="pi pi-times" severity="danger" @click="selectedActionUserId = null" />
                        </InputGroup>
                        <LoadingState v-else small ></LoadingState>
                    </div>
                </template>
                <template #filter="{ filterModel }">
                    <MultiSelect v-model="filterModel.value" :options="users" optionValue="id" optionLabel="email" placeholder="Any" class="p-column-filter">
                        <template #option="slotProps">
                            <div class="flex items-center gap-2">
                                <span>{{ slotProps.option.email }}</span>
                            </div>
                        </template>
                    </MultiSelect>
                </template>
            </Column>
            <Column header="Actions">
                <template #body="{ data }">
                    <div class="flex gap-2">
                        <Button icon="pi pi-pencil" text raised rounded v-tooltip="'Edit group name'" @click="showEdit(data)" data-cy="edit-group"/>
                        <Button icon="pi pi-times-circle" text raised rounded severity="danger" v-tooltip="'Delete Group'" @click="showDelete(data)" data-cy="delete-group" />
                    </div>
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';

import {LOGIN_API_TYPE} from "@/src/model/apiTypes";

import { useStore } from 'vuex';
const store = useStore();

const groups = computed(() => store.getters['userStore/groups']);

const filters = ref();

const loading = computed(() => store.getters.isLoading([LOGIN_API_TYPE.GET_GROUPS]))


const initFilters = () => {
    filters.value = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        name: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    };
};

initFilters();

const clearFilter = () => {
    initFilters();
};

const showDialog = ref(false);

const group = ref({
    name: ''
});
const addGroup = () => {
    selectedActionUserId.value = null;
    group.value = {
        name: ''
    };
    showDialog.value = true;
};

const saveGroup = () => {
    if (selectedActionUserId.value) {
        store.dispatch('userStore/editGroup', { id: selectedActionUserId.value, data: group.value }).then(() => {
            showDialog.value = false;
        });
        return;
    }
    store.dispatch('userStore/addGroup', group.value).then(() => {
        showDialog.value = false;
    });
};

const selectedActionUserId = ref(null)

const showDeleteDialog = ref(false)
const showDelete = async (data) => {
    selectedActionUserId.value = data.id
    showDeleteDialog.value = true
}

const deleteGroup = async () => {
    store.dispatch('userStore/deleteGroup', selectedActionUserId.value).then(() => {
        showDeleteDialog.value = false
    })
}

const showEdit = data => {
    selectedActionUserId.value = data.id
    group.value = {
        name: data.name
    }
    showDialog.value = true
}

const editType = ref(null)

const users = computed(() => store.getters['userStore/users'])

const usersToEdit  = ref([])
const editUsers = (data) => {
    editType.value = 'users'
    selectedActionUserId.value = data.id
    usersToEdit.value = data.users.map(user => user.id)
}

const updateUsers = async () => {
    await store.dispatch('userStore/editGroup', {
        id: selectedActionUserId.value,
        data: {
            users: usersToEdit.value
        }
    })
    selectedActionUserId.value = null
}
</script>
